import React from 'react';
import * as Sentry from '@sentry/react';

import {ToastType} from 'common/components/toast/types';
import {addToast} from 'common/components/toast-area/toast-manager';
import {TrackingEvent, TrackingProperty, trackOnboardingEvent} from 'common/onboarding/immersive-onboarding/tracking';
import {getProjectTallies} from 'common/report/queries/app-api/immersive-onboarding';
import {ApiClient} from 'common/onboarding/immersive-onboarding/api-client';
import {ProjectTallies, SetupGuideType, Step} from 'common/onboarding/immersive-onboarding/types';
import {useTranslation} from 'common/i18n/hooks';

interface StepCompletionParams {
  setupGuideInfo: SetupGuideType;
  setSetupGuideInfo: React.Dispatch<React.SetStateAction<SetupGuideType>>;
  setStep: React.Dispatch<React.SetStateAction<Step>>;
  completedStep: Step;
  nextStep: Step;
  trackingEvent: TrackingEvent;
  trackingData?: {
    [key: string]: any;
  };
}

/**
 * Complete a step in the setup process
 */
export const handleCompleteStep = ({
  setupGuideInfo,
  setSetupGuideInfo,
  setStep,
  completedStep,
  nextStep,
  trackingEvent,
  trackingData = {},
}: StepCompletionParams): void => {
  const updatedStepsCompleted: Set<Step> = new Set(setupGuideInfo.stepsCompleted);
  updatedStepsCompleted.add(completedStep);

  // Update API and state
  ApiClient.updateCompleted(Number(setupGuideInfo.projectId), updatedStepsCompleted);
  setSetupGuideInfo({
    ...setupGuideInfo,
    stepsCompleted: updatedStepsCompleted,
  });
  setStep(nextStep);

  // Track event
  trackOnboardingEvent(trackingEvent, {
    [TrackingProperty.CurrentStep]: nextStep,
    ...trackingData,
  });
};

export interface ProjectTalliesState {
  data: ProjectTallies | null;
  isLoading: boolean;
  hasError: string | null;
  shouldPoll?: boolean;
  talliesStatus: {
    events: boolean;
    users: boolean;
    sessionRecordings: boolean;
  };
}

interface UseProjectTalliesOptions {
  pollUntilData?: boolean;
  pollInterval?: number;
  maxPollAttempts?: number;
}

export const useProjectTallies = (
  projectId: number,
  options: UseProjectTalliesOptions = {},
): ProjectTalliesState & {
  pollAttempts: number;
  isPollingComplete: boolean;
  hasTimedOut: boolean;
  refetch: (params: {resetPolling: boolean}) => Promise<void>;
} => {
  const {t} = useTranslation();
  const {pollUntilData = false, pollInterval = 5000, maxPollAttempts = 20} = options;
  const [data, setData] = React.useState<ProjectTallies | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [hasError, setError] = React.useState<string | null>(null);
  const [shouldPoll, setShouldPoll] = React.useState(pollUntilData);
  const [pollAttempts, setPollAttempts] = React.useState(0);
  const [isPollingComplete, setIsPollingComplete] = React.useState(!pollUntilData);

  const isMountedRef = React.useRef(true);

  React.useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const stopPolling = React.useCallback(() => {
    if (!isMountedRef.current) return;
    setShouldPoll(false);
    setIsPollingComplete(true);
  }, []);

  // Internal polling refetch
  const refetch = React.useCallback(
    async (_attemptNumber: number = 0) => {
      if (!isMountedRef.current) return;

      setIsLoading(true);
      setError(null);

      try {
        const tallies = await getProjectTallies(projectId);

        if (!isMountedRef.current) return;

        setData(tallies);

        if (pollUntilData) {
          const hasAnyData = tallies.hasEvents && tallies.hasUsers && tallies.hasSessionRecordings;

          if (hasAnyData) {
            stopPolling();
          }
        }
      } catch (err) {
        if (!isMountedRef.current) return;

        setError(err instanceof Error ? err.message : `Failed to fetch tallies`);
        setData(null);

        addToast({
          type: ToastType.ERROR,
          message: t(`onboarding.verify.status-box.headings.no-connections-found`),
        });

        Sentry.withScope((scope) => {
          scope.setTag(`component`, `immersive-onboarding/utils::useProjectTallies`);
          scope.setContext(`refetch`, {
            action: `getProjectTallies`,
            projectId: String(projectId),
            attemptNumber: _attemptNumber,
          });
          Sentry.captureException(err);
        });
      } finally {
        if (isMountedRef.current) {
          setIsLoading(false);
        }
      }
    },
    [projectId, pollUntilData, stopPolling, t],
  );

  // Public refetch
  const publicRefetch = React.useCallback(
    async ({resetPolling}: {resetPolling: boolean}) => {
      if (!isMountedRef.current) return;

      if (resetPolling && pollUntilData) {
        setPollAttempts(0);
        setShouldPoll(true);
        setIsPollingComplete(false);
      }
      await refetch(0);
    },
    [refetch, pollUntilData],
  );

  // Initial fetch
  React.useEffect(() => {
    refetch(0);
    setPollAttempts(0);
  }, [projectId, refetch]);

  // Polling effect
  React.useEffect(() => {
    if (!shouldPoll) {
      return undefined;
    }

    const interval = setInterval(() => {
      setPollAttempts((prev) => {
        const newAttempts = prev + 1;

        if (newAttempts >= maxPollAttempts) {
          stopPolling();
          return newAttempts;
        }

        if (isMountedRef.current) {
          refetch(newAttempts);
        }
        return newAttempts;
      });
    }, pollInterval);

    return () => {
      clearInterval(interval);
    };
  }, [shouldPoll, pollInterval, maxPollAttempts, refetch, stopPolling]);

  const talliesStatus = React.useMemo(() => {
    if (!data) {
      return {
        events: false,
        users: false,
        sessionRecordings: false,
      };
    }

    return {
      events: data.hasEvents,
      users: data.hasUsers,
      sessionRecordings: data.hasSessionRecordings,
    };
  }, [data]);

  const hasTimedOut = isPollingComplete && pollAttempts >= maxPollAttempts;

  return {
    data,
    isLoading,
    hasError,
    talliesStatus,
    pollAttempts,
    isPollingComplete,
    hasTimedOut,
    refetch: publicRefetch,
  };
};

export const calculatePollingInterval = ({
  intervalSeconds,
  totalMinutes,
}: {
  intervalSeconds: number;
  totalMinutes: number;
}) => {
  // default to 20 seconds interval and 3 attempts if invalid input
  if (intervalSeconds <= 0 || totalMinutes <= 0)
    return {
      intervalMs: 20 * 1000, // default to 20 seconds
      totalAttempts: 3,
    };
  const intervalMs = intervalSeconds * 1000;
  const totalMs = totalMinutes * 60 * 1000;
  const totalAttempts = Math.ceil(totalMs / intervalMs);

  return {
    intervalMs,
    totalAttempts,
  };
};

export function eligibleForAiWizard() {
  // @Jun to update once we have list of interested customers
  // const eligibleProjects = [3];
  return false;
}
